import { useCallback, useEffect, useRef } from 'react';

export function useTableKeyboardNav({
  onEnterEdit,
  enterMovesDown = true,
  printableEnters = true,
}) {
  const wrapperRef = useRef(null);

  const getRows = useCallback(() => {
    const root = wrapperRef.current;
    if (!root) return [];

    return Array.from(root.querySelectorAll('.ant-table-tbody > tr'))
      .filter((tr) => tr.offsetParent !== null);
  }, []);

  const getCells = useCallback((row) => {
    return Array.from(row.querySelectorAll('td[tabindex="0"]'));
  }, []);

  const focusCellByElement = useCallback((currentTd, rowDelta, colDelta) => {
    const rows = getRows();
    const currentRow = currentTd.closest('tr');
    const rowIndex = rows.indexOf(currentRow);
    if (rowIndex === -1) return;

    const currentCells = getCells(currentRow);
    const colIndex = currentCells.indexOf(currentTd);
    if (colIndex === -1) return;

    const nextRowIndex = Math.max(0, Math.min(rows.length - 1, rowIndex + rowDelta));
    const nextRow = rows[nextRowIndex];
    const nextCells = getCells(nextRow);

    const nextColIndex = Math.max(
      0,
      Math.min(nextCells.length - 1, colIndex + colDelta)
    );

    const target = nextCells[nextColIndex];
    target?.focus();
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [getRows, getCells]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const onKeyDown = (e) => {
      const td = e.target.closest('td[tabindex="0"]');
      if (!td || !root.contains(td)) return;

      const input = td.querySelector('input, textarea');
      const isEditing = input && document.activeElement === input;

      if (isEditing) {
        if (e.key === 'Enter' && enterMovesDown) {
          setTimeout(() => focusCellByElement(td, 1, 0), 0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          focusCellByElement(td, -1, 0);
          break;

        case 'ArrowDown':
          e.preventDefault();
          focusCellByElement(td, 1, 0);
          break;

        case 'ArrowLeft':
          e.preventDefault();
          focusCellByElement(td, 0, -1);
          break;

        case 'ArrowRight':
          e.preventDefault();
          focusCellByElement(td, 0, 1);
          break;

        case 'Tab':
          e.preventDefault();
          focusCellByElement(td, 0, e.shiftKey ? -1 : 1);
          break;

        case 'Enter':
        case 'F2':
          e.preventDefault();
          onEnterEdit?.(null, null, td);
          break;

        default:
          if (
            printableEnters &&
            e.key.length === 1 &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey
          ) {
            onEnterEdit?.(null, null, td);
          }
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => root.removeEventListener('keydown', onKeyDown);
  }, [focusCellByElement, onEnterEdit, enterMovesDown, printableEnters]);

  return { wrapperRef };
}